import { Core } from "@faire/design-tokens";
import { MobileAppIcon } from "@faire/retailer-visitor-shared/icons/MobileAppIcon";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { IconButton } from "@faire/web--source/slate/IconButton";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { Close } from "@faire/web--source/slate/icons/foundation/Close";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import React from "react";
import styled from "styled-components";

interface IProps {
  message: React.ReactNode;
  ctaText: string;
  ctaLink?: string;
  mediumMessageFont?: boolean;
  ctaAction: () => void;
  closeAction: () => void;
  icon?: React.ReactNode;
  noIcon?: boolean;
  cornerButton?: boolean;
}

export const MobileFixedBanner: React.FC<IProps> = ({
  message,
  ctaText,
  ctaLink,
  ctaAction,
  closeAction,
  mediumMessageFont,
  icon,
  noIcon,
  cornerButton,
}) => {
  const { strictLocalize } = useStrictLocalization();

  return (
    <StyledRow align="flex-start">
      <Row
        role="button"
        onClick={() => {
          ctaAction();
          if (ctaLink) {
            getWindow()?.open(ctaLink, "_blank");
          }
        }}
      >
        {!noIcon ? (
          <>
            <MobileAppIcon size="48px" icon={icon} />
            <Spacer width="2x" />
          </>
        ) : null}
        <Column>
          <Typography
            color={Core.text.inverse}
            variant={
              mediumMessageFont ? "paragraphSansMedium" : "paragraphSansRegular"
            }
          >
            {message}
          </Typography>
          <Spacer height="1x" />
          <Row align="center">
            <DummyLink color={Core.text.inverse}>{ctaText}</DummyLink>
            <Spacer width="1x" />
            <Arrow direction="right" color={Core.icon.inverse} />
          </Row>
        </Column>
      </Row>
      {cornerButton ? (
        <>
          <Spacer width="5x" />
          <AbsoluteIconButton
            onClick={closeAction}
            aria-label={strictLocalize({
              defaultMessage: "Dismiss Banner at the bottom of screen",
              description: {
                text: "Accessibility label for the close button on the mobile app banner at the bottom of screen",
              },
            })}
            size="small"
          >
            <Close color={Core.icon.inverse} />
          </AbsoluteIconButton>
        </>
      ) : (
        <Column>
          <Spacer height="1x" />
          <IconButton
            onClick={closeAction}
            aria-label={strictLocalize({
              defaultMessage: "Dismiss Banner at the bottom of screen",
              description: {
                text: "Accessibility label for the close button on the mobile app banner at the bottom of screen",
              },
            })}
            size="small"
          >
            <Close color={Core.icon.inverse} />
          </IconButton>
        </Column>
      )}
    </StyledRow>
  );
};

const StyledRow = styled(Row)`
  width: 100%;
  background: ${Core.surface.default};
  padding: 16px;
`;

const DummyLink = styled(Typography)`
  text-decoration: underline;
`;

const AbsoluteIconButton = styled(IconButton)`
  position: absolute;
  top: ${getSpacing("1x")};
  right: ${getSpacing("1x")};
`;
